import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LazyImageStyle } from "@insite/mobius/LazyImage/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import qs from "qs";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const splitCommaSeparated = (value?: string | string[] | null) =>
    typeof value === "string" ? value.split(",") : undefined;

const fieldHasFilters = (field: string, filters: string[]) => {
    let hasFilters = false;
    const arr = field.split(",");
    arr.forEach(pName => {
        if (filters.indexOf(pName) > -1) {
            hasFilters = true;
        }
    });
    return hasFilters;
};

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    const location = getLocation(state);
    let links: SafeDictionary<PageLinkModel> = {};
    let newsListFilters: any = {};
    let filteredPages = pages.value;
    if (filteredPages) {
        links = filteredPages.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
        const parsedQuery = parseQueryString<{ tags?: string; category?: string }>(location.search);
        newsListFilters = {
            category: splitCommaSeparated(parsedQuery.category),
            tags: splitCommaSeparated(parsedQuery.tags),
        };

        if (newsListFilters.tags || newsListFilters.category) {
            filteredPages = filteredPages.filter(page => {
                if (newsListFilters.tags && page.fields.tags) {
                    return fieldHasFilters(page.fields.customTags as string, newsListFilters.tags);
                }
                if (newsListFilters.category && page.fields.categories && page.fields.categories.length > 0) {
                    return fieldHasFilters(page.fields.categories as string, newsListFilters.category);
                }

                return false;
            });
        }
    }

    return {
        parentNodeId: getCurrentPage(state).nodeId,
        location,
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
        filteredPages,
        newsListFilters,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    tagsAndCategoriesWrapper?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    listWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            background-color: #f3f3f3;
            padding: 0.275rem 1rem;
            .news-list-header {
                h2 {
                    margin-bottom: 0;
                }
            }

            .news-list-header.blog-sub-heading {
                font-size: 16px;
                padding-bottom: 25px;
                padding-top: 6px;
            }
        `,
    },
    titleText: {
        size: 20,
        css: css`
            display: inline-block;
            margin-top: 12px;
            order: 2;
            padding: 0 15px;
            line-height: 1px;
            margin-bottom: 22px;

            a {
                font-size: 20px;
                line-height: 26px;

                &:hover {
                    text-decoration: underline;
                    color: #666 !important;
                }
                span {
                    color: #666 !important;
                    font-family: "Roboto-Regular" !important;
                    font-weight: 500;
                }
            }
        `,
    },
    newsWrapper: {
        css: css`
            /* padding-top: 15px; */
            position: relative;
            border: 1px solid ${getColor("common.border")};
            box-shadow: 0 2px 4px #e4e4ed;
            display: flex;
            flex-direction: column;
            background-color: #fff;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            margin: 20px 0px;
                        `,
                        css`
                            width: 100%;
                            margin: 20px 0px;
                        `,
                        css`
                            width: 49%;
                            margin-right: 10px;
                            margin-top: 15px;
                            margin-bottom: 15px;
                            &:nth-child(2n) {
                                margin-right: 0px;
                            }
                        `,
                        css`
                            width: 32%;
                            margin-right: 10px;
                            margin-top: 15px;
                            margin-bottom: 15px;
                            &:nth-child(3n) {
                                margin-right: 0;
                            }
                        `,
                        css`
                            width: 32%;
                            margin-right: 10px;
                            margin-top: 15px;
                            margin-bottom: 15px;
                            &:nth-child(3n) {
                                margin-right: 0;
                            }
                        `,
                    ],
                    "minMax",
                );
            }}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
                width: 100%;
            `,
        },
    },
    dateAndAuthorWrapper: {
        css: css`
            display: inline-block;
            order: 3;
            padding: 0 15px;
            margin-top: auto;
            margin-bottom: 0.5em;

            em {
                font-style: unset;
                font-size: 13px;
                color: #999;
            }

            strong {
                font-weight: 500;
                margin-right: 0;
                font-size: 13px;
                color: #999;

                span {
                    color: #a22e31;
                    font-size: 14px;
                    padding-left: 5px;
                    font-family: "Roboto-Medium" !important;

                    i {
                        font-size: 5px;
                        padding-left: 5px;
                        padding-right: 2px;
                        position: relative;
                        bottom: 2px;
                        color: #999;
                    }
                }
            }
        `,
    },
    publishDateText: {
        css: css`
            margin-left: 0.5rem;
        `,
    },
    tagsAndCategoriesWrapper: {
        css: css`
            display: inline-block;
            order: 3;
            padding: 0 15px;
            margin-top: auto;
            margin-bottom: 0.5em;
            .tags-wrapper {
                display: inline-block;
                button.btn.primary {
                    text-transform: capitalize;
                    margin-right: 5px;
                    height: auto;
                    transition: all 0.4s;
                    margin-left: 0;
                    text-align: center;
                    display: inline-block;
                    font-family: "Roboto-Regular";
                    margin-bottom: 5px;
                    font-size: 0.875rem;
                    border-radius: 3px;
                    background-color: ${getColor("common.background")};
                    padding: 5px 5px;
                    border: #666 1.2px solid;
                    color: #666;

                    &:hover {
                        background: ${getColor("primary")};
                        color: ${getColor("secondary.contrast")};
                        border: ${getColor("secondary")} 1px solid;
                        cursor: pointer;
                    }
                }
            }
        `,
    },
    summaryWrapper: {
        css: css`
            order: 1;
            margin-bottom: 0;
            ${LazyImageStyle} {
                display: flex;
                justify-content: flex-start;
                align-items: flex-start;
                width: 100% !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: auto !important;
                        `,
                        css`
                            width: auto !important;
                        `,
                        null,
                        null,
                        null,
                    ])}
                img {
                    width: 100%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 100% !important;
                            `,
                            css`
                                width: 100% !important;
                            `,
                            css`
                                width: 100% !important;
                            `,
                            css`
                                width: 100% !important;
                            `,
                            null,
                        ])}
                }
            }
            .bl-list-wrapper {
                display: flex;
                flex-direction: column;

                .bl-img {
                    width: 100%;
                    a {
                        width: 100%;
                    }
                    span {
                        width: 100%;
                    }
                    img {
                        border-bottom: 1px solid #efefef;
                        max-height: 250px;
                        will-change: initial !important;
                    }
                    iframe {
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                }

                .bl-disc {
                    width: 100%;
                    padding: 0 20px;
                    font-size: 14px;
                    line-height: 25px;
                }
            }

            a.tst_newsListView_newsPage_readMoreLink {
                display: none;
            }
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number, newsFilters?: any) {
        const filters = newsFilters || this.props.newsListFilters || undefined;
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        if (filters) {
            const filterQuery = qs.stringify(filters, { arrayFormat: "comma", skipNulls: true });
            if (filterQuery) {
                newLocation += `&${filterQuery}`;
            }
        }
        history.push(newLocation);
    }

    updateFilters = (name: string, value: string) => {
        // const newsListFilters = { ...this.props.newsListFilters };
        // if (newsListFilters[name] && newsListFilters[name].length > 0 && newsListFilters[name].indexOf(value) > -1) {
        //     newsListFilters[name] =
        //         newsListFilters[name]?.length > 1
        //             ? newsListFilters[name].filter((pFilter: string) => value !== pFilter)
        //             : undefined;
        // } else {
        //     const currentFilters = newsListFilters[name] ? newsListFilters[name] : [];
        //     currentFilters.push(value);
        //     newsListFilters[name] = currentFilters;
        // }

        const newsListFilters = { [name]: value };

        this.updateQueryString(1, this.props.parameter.pageSize, newsListFilters);
    };

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
            filteredPages,
            newsListFilters,
        } = this.props;

        if (!pages || !filteredPages) {
            return null;
        }

        const totalItems = filteredPages.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(filteredPages, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        return (
            <StyledWrapper {...styles.listWrapper}>
                {sortedAndPagedPages.map((page: PageProps) => (
                    <StyledWrapper {...styles.newsWrapper} key={page.id} className="news-list-card">
                        <Typography variant="h2" {...styles.titleText} className="news-heading">
                            <Link
                                href={links[page.nodeId]?.url}
                                data-test-selector="newslist_pagetitle"
                                className="tst_newsListView_newsPage_pageTitleLink"
                            >
                                {page.fields["title"]}
                            </Link>
                        </Typography>
                        <StyledWrapper {...styles.dateAndAuthorWrapper}>
                            {page.fields["author"] && (
                                <strong>
                                    By
                                    <Typography
                                        {...styles.authorText}
                                        data-test-selector="newslist_author"
                                        className="news-author"
                                    >
                                        {page.fields["author"]}
                                    </Typography>
                                </strong>
                            )}

                            {page.fields["publishDate"] && (
                                <Typography
                                    as="em"
                                    {...styles.publishDateText}
                                    data-test-selector="newslist_publishdate"
                                    className="news-date"
                                >
                                    <LocalizedDateTime
                                        dateTime={new Date(page.fields["publishDate"])}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </Typography>
                            )}
                        </StyledWrapper>
                        {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                            <StyledWrapper
                                {...styles.summaryWrapper}
                                data-test-selector="newslist_content"
                                className="news-page-summary"
                            >
                                {parse(
                                    page.fields["newsSummary"] || truncate(page.fields["newsContent"], { length: 500 }),
                                    parserOptions,
                                )}
                            </StyledWrapper>
                        )}
                        {/* <Link
                            href={links[page.nodeId]?.url}
                            {...styles.readMoreLink}
                            data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                        >
                            {translate("Read More")}
                        </Link> */}
                        <StyledWrapper {...styles.tagsAndCategoriesWrapper}>
                            {page.fields["customTags"] && page.fields["customTags"].trim() !== "" ? (
                                <StyledWrapper className="tags-wrapper">
                                    {page.fields["customTags"].split(",").map((pTag: string, index: number) => (
                                        <button
                                            onClick={() => this.updateFilters("tags", pTag)}
                                            className="btn primary"
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${pTag}-${index}`}
                                        >
                                            {pTag}
                                        </button>
                                    ))}
                                </StyledWrapper>
                            ) : null}
                            {page.fields["categories"] && page.fields["categories"].trim() !== "" ? (
                                <StyledWrapper className="tags-wrapper">
                                    {page.fields["categories"].split(",").map((pCat: string, index: number) => {
                                        const cat = pCat.trim();
                                        return (
                                            <button
                                                onClick={() => this.updateFilters("category", cat)}
                                                className="btn primary"
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${cat}-${index}`}
                                            >
                                                {cat}
                                            </button>
                                        );
                                    })}
                                </StyledWrapper>
                            ) : null}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
                {totalItems > defaultPageSize && (
                    <Pagination
                        resultsCount={totalItems}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageOptions={[
                            defaultPageSize,
                            defaultPageSize * 2,
                            defaultPageSize * 3,
                            defaultPageSize * 4,
                        ]}
                        onChangePage={this.changePage}
                        onChangeResultsPerPage={this.changeResultsPerPage}
                        pageSizeCookie="NewsList-PageSize"
                        {...styles.pagination}
                    />
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
